import React, { useState, useMemo } from 'react';
import { useSignals } from '@/hooks/useDashboardQueries';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { StatusBadge } from '@/components/common/StatusBadge';
import { DataTable, Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { TableSkeleton, MetricCardSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { formatTimestamp } from '@/lib/utils';
import { Signal } from '@/types/trading';
import {
  Zap,
  Search,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  ShieldCheck,
  ShieldAlert,
  Hash,
  Link2,
} from 'lucide-react';

const FAILED_STATUSES = ['ORDER_REJECTED', 'ORDER_FAILED', 'EXECUTION_UNKNOWN'];

export const SignalsView: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const {
    data: signalsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useSignals();

  const signals = useMemo(() => signalsData?.signals || [], [signalsData]);

  // Filtered dataset
  const filteredSignals = useMemo(() => {
    return signals.filter((sig) => {
      const currentStatus = String(sig.status || sig.validation_status || '').toUpperCase();
      const signalIdStr = String(sig.raw_id ?? sig.id ?? '');
      const orderIdStr = String(sig.order_id ?? '');
      const positionIdStr = String(sig.position_id ?? '');
      const symbolStr = sig.parsed_symbol || '';
      const rawMsgStr = sig.raw_message || '';
      const reasonStr = sig.outcome_reason || sig.rejection_reason || '';
      const codeStr = sig.outcome_reason_code || '';

      const term = searchTerm.toLowerCase();
      const matchesSearch =
        !term ||
        symbolStr.toLowerCase().includes(term) ||
        rawMsgStr.toLowerCase().includes(term) ||
        reasonStr.toLowerCase().includes(term) ||
        codeStr.toLowerCase().includes(term) ||
        signalIdStr.toLowerCase().includes(term) ||
        `sig-${signalIdStr}`.toLowerCase().includes(term) ||
        `#sig-${signalIdStr}`.toLowerCase().includes(term) ||
        orderIdStr.toLowerCase().includes(term) ||
        `ord-${orderIdStr}`.toLowerCase().includes(term) ||
        `#ord-${orderIdStr}`.toLowerCase().includes(term) ||
        positionIdStr.toLowerCase().includes(term) ||
        `pos-${positionIdStr}`.toLowerCase().includes(term) ||
        `#pos-${positionIdStr}`.toLowerCase().includes(term);

      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'FAILED_ORDERS'
          ? FAILED_STATUSES.includes(currentStatus)
          : currentStatus === statusFilter);

      return matchesSearch && matchesStatus;
    });
  }, [signals, searchTerm, statusFilter]);

  // Summary Metrics (Computed across full signal dataset)
  const totalSignals = signals.length;
  const positionsOpened = useMemo(
    () => signals.filter((s) => (s.status || s.validation_status) === 'POSITION_OPENED').length,
    [signals]
  );
  const blockedByRisk = useMemo(
    () => signals.filter((s) => (s.status || s.validation_status) === 'BLOCKED_RISK').length,
    [signals]
  );
  const rejectedOrFailed = useMemo(
    () =>
      signals.filter((s) => {
        const st = String(s.status || s.validation_status);
        return ['REJECTED', 'ORDER_REJECTED', 'ORDER_FAILED', 'EXECUTION_UNKNOWN'].includes(st);
      }).length,
    [signals]
  );
  const conversionRate =
    totalSignals > 0 ? ((positionsOpened / totalSignals) * 100).toFixed(1) : '0.0';

  // Table Columns Definition
  const columns: Column<Signal>[] = [
    {
      key: 'id',
      header: 'Signal ID & Time',
      sortable: true,
      render: (sig) => {
        const idDisplay = sig.raw_id !== undefined && sig.raw_id !== null ? sig.raw_id : sig.id;
        return (
          <div className="space-y-0.5">
            <div className="flex items-center gap-1">
              <Hash className="w-3 h-3 text-slate-500" />
              <span className="font-mono text-cyan-300 font-bold text-xs">
                SIG-{idDisplay}
              </span>
            </div>
            <span className="font-mono text-slate-400 text-[10px] flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5 text-slate-500" />
              {formatTimestamp(sig.timestamp, 'compact')}
            </span>
          </div>
        );
      },
    },
    {
      key: 'parsed_symbol',
      header: 'Symbol / Side',
      sortable: true,
      render: (sig) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-slate-100 font-mono text-sm">
              {sig.parsed_symbol || '—'}
            </span>
            <span className="px-1.5 py-0.2 rounded bg-slate-800 text-[10px] font-mono text-slate-400">
              NSE
            </span>
          </div>
          {sig.signal_type ? (
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-mono font-bold uppercase border ${
                sig.signal_type === 'BUY'
                  ? 'bg-emerald-950/70 text-emerald-400 border-emerald-800/60'
                  : 'bg-rose-950/70 text-rose-400 border-rose-800/60'
              }`}
            >
              {sig.signal_type === 'BUY' ? (
                <ArrowUpRight className="w-2.5 h-2.5 text-emerald-400" />
              ) : (
                <ArrowDownRight className="w-2.5 h-2.5 text-rose-400" />
              )}
              {sig.signal_type}
            </span>
          ) : (
            <span className="text-slate-500 text-xs font-mono">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Lifecycle Status',
      align: 'center',
      render: (sig) => (
        <StatusBadge status={sig.status || sig.validation_status} size="sm" showDot={true} />
      ),
    },
    {
      key: 'outcome_reason',
      header: 'Outcome / Risk Reason',
      render: (sig) => {
        const reason = sig.outcome_reason || sig.rejection_reason;
        const code = sig.outcome_reason_code;
        if (!code && !reason) {
          return <span className="text-slate-500 font-mono text-xs">—</span>;
        }
        return (
          <div className="space-y-1 max-w-sm">
            {code && (
              <span className="inline-block px-1.5 py-0.5 rounded bg-slate-900 border border-slate-800 text-[10px] font-mono text-amber-400 font-semibold tracking-wide">
                {code}
              </span>
            )}
            {reason && (
              <span
                className="text-xs font-mono text-slate-300 truncate block"
                title={reason}
              >
                {reason}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'order_id',
      header: 'Order & Position Correlation',
      render: (sig) => {
        const hasOrder = Boolean(sig.order_id);
        const hasPosition = Boolean(sig.position_id);
        const attempted = Boolean(sig.order_attempted);

        if (!hasOrder && !hasPosition && !attempted) {
          return <span className="text-slate-500 font-mono text-xs">—</span>;
        }

        return (
          <div className="space-y-1 text-xs font-mono">
            {hasOrder ? (
              <div className="flex items-center gap-1 text-cyan-300">
                <Link2 className="w-3 h-3 text-cyan-400" />
                <span>ORD-{sig.order_id}</span>
                {sig.execution_status && (
                  <span className="text-[10px] text-slate-400">({sig.execution_status})</span>
                )}
              </div>
            ) : attempted ? (
              <span className="text-amber-400 text-[11px] font-semibold">Order Attempted</span>
            ) : null}

            {hasPosition && (
              <div className="flex items-center gap-1 text-emerald-400 text-[11px]">
                <span>POS-{sig.position_id}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'risk_decision_at',
      header: 'Risk Decision Time',
      sortable: true,
      render: (sig) => (
        <span className="font-mono text-slate-400 text-xs">
          {formatTimestamp(sig.risk_decision_at, 'compact')}
        </span>
      ),
    },
    {
      key: 'raw_message',
      header: 'Raw Channel Message',
      render: (sig) => {
        if (!sig.raw_message) {
          return <span className="text-xs font-mono text-slate-600 italic">Not available</span>;
        }
        return (
          <span
            className="text-xs font-mono text-slate-400 max-w-xs truncate block bg-slate-950/40 px-2 py-1 rounded border border-slate-800/80"
            title={sig.raw_message}
          >
            {sig.raw_message}
          </span>
        );
      },
    },
  ];

  if (isLoading && signals.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Signals & Events"
          icon={Zap}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <TableSkeleton rows={5} cols={7} />
      </div>
    );
  }

  if (isError && signals.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Signals & Events"
          icon={Zap}
        />
        <ErrorState
          title="Failed to load signals feed"
          message={error instanceof Error ? error.message : 'API connection error'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="animate-stagger-1">
        <PageHeader
          title="Signals & Events Feed"
          description="Authoritative stream of Telegram BUY signals, risk decision gates, reason codes, and order/position conversion states."
          icon={Zap}
          badge={
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-2.5 py-0.5 rounded-full bg-slate-900 text-slate-300 border border-slate-800">
                {totalSignals} TOTAL SIGNALS
              </span>
              <StatusBadge status="PAPER_MODE" label="PAPER MODE" size="sm" />
            </div>
          }
        />
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 animate-stagger-2">
        <MetricCard
          title="Total Signals"
          value={totalSignals}
          subtitle="Processed from Telegram stream"
          variant="default"
          icon={Zap}
        />

        <MetricCard
          title="Positions Opened"
          value={positionsOpened}
          subtitle="Filled and position active"
          variant="emerald"
          icon={CheckCircle2}
        />

        <MetricCard
          title="Blocked by Risk"
          value={blockedByRisk}
          subtitle="Declined at risk guardrails"
          variant={blockedByRisk > 0 ? 'amber' : 'default'}
          icon={ShieldAlert}
        />

        <MetricCard
          title="Rejected / Failed"
          value={rejectedOrFailed}
          subtitle="Parser, broker reject or fail"
          variant={rejectedOrFailed > 0 ? 'crimson' : 'default'}
          icon={AlertTriangle}
        />

        <MetricCard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          subtitle="Position-opened / Total signals"
          variant="cyan"
          icon={ShieldCheck}
        />
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900/40 animate-stagger-3">
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search symbol, notes, raw text, IDs..."
              className="w-full pl-9 pr-3 py-1.5 text-xs font-mono bg-slate-950/80 border border-slate-800 rounded-md text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all duration-150"
            />
          </div>

          <div className="flex items-center gap-1 text-xs font-mono flex-wrap">
            {[
              { key: 'ALL', label: 'ALL' },
              { key: 'POSITION_OPENED', label: 'POSITION OPENED' },
              { key: 'BLOCKED_RISK', label: 'BLOCKED RISK' },
              { key: 'REJECTED', label: 'REJECTED' },
              { key: 'RECEIVED', label: 'RECEIVED' },
              { key: 'FAILED_ORDERS', label: 'FAILED ORDERS' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-2.5 py-1 rounded text-[11px] font-mono transition-all duration-150 active:scale-95 ${
                  statusFilter === f.key
                    ? 'bg-cyan-950/80 text-cyan-300 border border-cyan-800/70 font-semibold'
                    : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="text-xs font-mono text-slate-400">
          Showing {filteredSignals.length} of {signals.length} signals
        </div>
      </div>

      {/* Desktop Dense Table */}
      <div className="hidden md:block animate-stagger-4">
        <DataTable
          data={filteredSignals}
          columns={columns}
          keyExtractor={(sig) => String(sig.raw_id ?? sig.id)}
          pageSize={10}
          emptyTitle="No alerts yet."
          emptyDescription="Trading events and system notifications will appear here."
        />
      </div>

      {/* Mobile / Tablet Responsive Cards */}
      <div className="md:hidden space-y-3 animate-stagger-4">
        {filteredSignals.length === 0 ? (
          <EmptyState
            title="No alerts yet."
            description="Trading events and system notifications will appear here."
          />
        ) : (
          filteredSignals.map((sig) => {
            const idDisplay = sig.raw_id !== undefined && sig.raw_id !== null ? sig.raw_id : sig.id;
            const reason = sig.outcome_reason || sig.rejection_reason;
            const code = sig.outcome_reason_code;
            const sideClass =
              sig.signal_type === 'BUY'
                ? 'card-trade-buy'
                : sig.signal_type === 'SELL'
                ? 'card-trade-sell'
                : 'border-slate-800 bg-slate-900/60';

            return (
              <div
                key={String(sig.raw_id ?? sig.id)}
                className={`p-4 rounded-lg border space-y-3 interactive-card active:scale-[0.99] transition-all duration-150 ${sideClass}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-cyan-400 font-bold">
                      #SIG-{idDisplay}
                    </span>
                    <span className="font-bold text-slate-100 font-mono text-base">
                      {sig.parsed_symbol || '—'}
                    </span>
                    {sig.signal_type && (
                      <span
                        className={`px-1.5 py-0.2 rounded text-[10px] font-mono font-bold uppercase border ${
                          sig.signal_type === 'BUY'
                            ? 'bg-emerald-950/70 text-emerald-400 border-emerald-800/60'
                            : 'bg-rose-950/70 text-rose-400 border-rose-800/60'
                        }`}
                      >
                        {sig.signal_type}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={sig.status || sig.validation_status} size="sm" showDot={true} />
                </div>

                <div className="space-y-1 text-xs font-mono pt-2 border-t border-slate-800/60">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">RECEIVED</span>
                    <span className="text-slate-300 tabular-nums">
                      {formatTimestamp(sig.timestamp, 'compact')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">RISK DECISION</span>
                    <span className="text-slate-300 tabular-nums">
                      {formatTimestamp(sig.risk_decision_at, 'compact')}
                    </span>
                  </div>
                  {(sig.order_id || sig.position_id || sig.order_attempted) && (
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[10px] text-slate-500">CORRELATION</span>
                      <div className="text-right">
                        {sig.order_id && (
                          <span className="text-cyan-400 block font-bold">#ORD-{sig.order_id}</span>
                        )}
                        {sig.position_id && (
                          <span className="text-emerald-400 block">#POS-{sig.position_id}</span>
                        )}
                        {!sig.order_id && sig.order_attempted && (
                          <span className="text-amber-400 text-[10px]">Order Attempted</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {(code || reason) && (
                  <div className="p-2.5 rounded bg-slate-950/70 border border-slate-800 text-[11px] font-mono space-y-1">
                    {code && (
                      <span className="text-[10px] text-amber-400 font-bold uppercase block">
                        Reason Code: {code}
                      </span>
                    )}
                    {reason && <span className="text-slate-300 block">{reason}</span>}
                  </div>
                )}

                <div className="p-2 rounded bg-slate-950/60 border border-slate-800 text-[10px] font-mono text-slate-400 break-words">
                  <span className="text-[9px] text-slate-500 block uppercase mb-0.5">Raw Telegram Message</span>
                  <span>{sig.raw_message || 'Not available'}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
